import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';

type ToastKind = 'success' | 'error' | 'info';
interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastContextValue {
  show: (message: string, kind?: ToastKind) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

/**
 * Linker Farbbalken über ein eigenes Element statt gemischter
 * `border-{color}`/`border-l-{color}`-Utility-Klassen - beide setzen
 * dieselbe CSS-Eigenschaft (`border-left-color`) und welche davon im
 * generierten Stylesheet gewinnt, ist von der JSX-Reihenfolge unabhängig
 * und damit nicht verlässlich vorhersehbar.
 */
const kindAccentClasses: Record<ToastKind, string> = {
  success: 'bg-success-500',
  error: 'bg-danger-500',
  info: 'bg-accent-500',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(0);

  const show = useCallback((message: string, kind: ToastKind = 'info') => {
    const id = nextId.current++;
    setToasts((prev) => [...prev, { id, kind, message }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 5000);
  }, []);

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      {createPortal(
        <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2">
          {toasts.map((t) => (
            <div
              key={t.id}
              className="animate-rise-in relative overflow-hidden rounded-md border border-surface-600 bg-surface-800 py-3 pl-5 pr-4 text-sm text-ivory-100 shadow-popover"
            >
              <span className={clsx('absolute inset-y-0 left-0 w-1.5', kindAccentClasses[t.kind])} />
              {t.message}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast muss innerhalb von <ToastProvider> verwendet werden.');
  return ctx;
}
